import { AppstoreOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Col, Row, Spin, message, Radio, Tooltip, Select, Modal } from "antd";
import CommonButton from "components/ButtonWidgets/CommonButton/CommonButton";
import React, { useState } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { apiDeleteUiForm, apiGetUiForms, apiCloneUiForms, apiGetRegistrationList } from "services/dashboardService";
import { showFormUiAddModal } from "./FormUI/showFormUiAddModal";
import FormUiTable from "./FormUI/FormUiTable";
import FormUiGroupedView from "./FormUI/FormUiGroupedView";

const Step1FormTab = (props) => {
  const { registrationID, setRegistrationID, settingInfo, registrationType } = props;
  const userDataStore = useSelector((x) => x.userDataStore);
  const appDataStore = useSelector((x) => x.appDataStore);

  const [dataList, setDataList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [viewMode, setViewMode] = useState("grouped"); // "grouped" or "table"
  const [cloneOptions, setCloneOptions] = useState([]);
  const [cloneFromId, setCloneFromId] = useState(undefined);
  const [cloneLoading, setCloneLoading] = useState(false);

  useEffect(() => {
    if (registrationID) {
      loadData();
    }
  }, [registrationID, settingInfo?.registration_info?.registration_type]);

  useEffect(() => {
    if (appDataStore.club?.id) {
      loadCloneOptions();
    }
  }, [appDataStore.club?.id, registrationID]);

  const loadCloneOptions = () => {
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("club_id", appDataStore.club.id);
    apiGetRegistrationList(params)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setCloneOptions(list.filter((x) => String(x.ID) !== String(registrationID)));
      })
      .catch(() => {
        setCloneOptions([]);
      });
  };

  const doClone = (sourceId) => {
    setCloneLoading(true);
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("source_registration_id", sourceId);
    params.append("target_registration_id", registrationID);
    apiCloneUiForms(params)
      .then((res) => {
        setCloneLoading(false);
        setCloneFromId(undefined);
        message.success(`Cloned ${res?.cloned_count ?? ""} field(s) successfully`);
        loadData();
      })
      .catch((err) => {
        setCloneLoading(false);
        setCloneFromId(undefined);
        message.error(err || "Unable to clone fields");
      });
  };

  const onCloneFrom = (sourceId) => {
    setCloneFromId(sourceId);
    const source = cloneOptions.find((x) => String(x.ID) === String(sourceId));
    const sourceName = source?.registration_name || "the selected registration";

    if (dataList.length > 0) {
      Modal.confirm({
        title: "Clone Fields",
        content: `This registration already has ${dataList.length} field(s). Cloning will add all fields from "${sourceName}" in addition to them. Continue?`,
        okText: "Clone",
        onOk: () => doClone(sourceId),
        onCancel: () => setCloneFromId(undefined),
      });
    } else {
      Modal.confirm({
        title: "Clone Fields",
        content: `Clone all fields from "${sourceName}" into this registration?`,
        okText: "Clone",
        onOk: () => doClone(sourceId),
        onCancel: () => setCloneFromId(undefined),
      });
    }
  };

  // Get current registration type
  const currentRegType = registrationType || settingInfo?.registration_info?.registration_type || "individual";

  const onAdd = async (presetGroup = null) => {
    var resInfo = await showFormUiAddModal({
      title: "Add UI Element for Step1",
      token: userDataStore.token,
      columns: columns,
      registrationID: registrationID,
      presetGroup: presetGroup,
      registrationType: currentRegType,
    });
    if (resInfo == null) return;
    loadData();
  };

  const loadData = () => {
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("registration_id", registrationID);
    // Pass registration_type so backend returns correct columns
    params.append("registration_type", currentRegType);
    setIsBusy(true);
    apiGetUiForms(params)
      .then((res) => {
        setDataList(res.ui_list);
        setColumns(res.fields_list);
        setIsBusy(false);
      })
      .catch((err) => {
        setIsBusy(false);
        message.error(err);
      });
  };

  const updateData = async (info) => {
    var resInfo = await showFormUiAddModal({
      title: "Update UI Element for Step1",
      token: userDataStore.token,
      registrationID: registrationID,
      columns: columns,
      info: info,
      registrationType: currentRegType,
    });
    if (resInfo == null) return;
    loadData();
  };

  const deleteData = (id) => {
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("id", id);
    setIsBusy(true);
    apiDeleteUiForm(params)
      .then((res) => {
        setIsBusy(false);
        loadData();
      })
      .catch((err) => {
        setIsBusy(false);
        message.error(err);
      });
  };

  const handleAddToGroup = (group) => {
    onAdd({
      group_title: group.title,
      group_type: group.groupType,
    });
  };

  return (
    <div>
      <Spin spinning={isBusy}>
        <Row
          gutter={[16, 16]}
          style={{ marginTop: 20 }}
          justify={"space-between"}
          align="middle"
        >
          <Col>
            <div style={{ fontWeight: 600, fontSize: 16 }}>
              Registration Form Fields
            </div>
            <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
              Configure the fields that will appear on your registration form
            </div>
          </Col>
          <Col>
            <Row gutter={16} align="middle">
              <Col>
                <Radio.Group
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  <Tooltip title="Grouped View">
                    <Radio.Button value="grouped">
                      <AppstoreOutlined />
                    </Radio.Button>
                  </Tooltip>
                  <Tooltip title="Table View">
                    <Radio.Button value="table">
                      <UnorderedListOutlined />
                    </Radio.Button>
                  </Tooltip>
                </Radio.Group>
              </Col>
              <Col>
                <Select
                  placeholder="Clone From..."
                  style={{ width: 220 }}
                  value={cloneFromId}
                  loading={cloneLoading}
                  disabled={cloneLoading || cloneOptions.length === 0}
                  onChange={onCloneFrom}
                  options={cloneOptions.map((reg) => ({
                    value: reg.ID,
                    label: reg.registration_name,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Col>
              <Col>
                <CommonButton
                  onClick={() => {
                    onAdd();
                  }}
                >
                  Add New Field
                </CommonButton>
              </Col>
            </Row>
          </Col>
        </Row>

        <div style={{ marginTop: 20 }}>
          {viewMode === "grouped" ? (
            <FormUiGroupedView
              dataList={dataList}
              deleteData={deleteData}
              updateData={updateData}
              onAddToGroup={handleAddToGroup}
              registrationType={currentRegType}
            />
          ) : (
            <FormUiTable
              dataList={dataList}
              deleteData={deleteData}
              updateData={updateData}
              registrationType={currentRegType}
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default Step1FormTab;
